import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Alert, Box, Button, Chip, Grid, InputAdornment, Link, Paper, Stack, TextField, Typography } from "@mui/material";
import { Campaign, EditOutlined, LocalOffer } from "@mui/icons-material";
import { Product } from "../../../types";
import { formatCurrencyBDT } from "../../../utils/currency";
import { InlineEditField } from "./InlineEditField";
import { ComboPricesEditor } from "./ComboPricesEditor";
import { RunSave, SavePatch } from "./useProductQuickSave";

const DATE = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Dhaka",
});
const taka = (n: number) => formatCurrencyBDT(n);

const price = (v: string) => {
    if (v === "") return "Enter a price.";
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? null : "Enter a valid price.";
};

function marginText(selling: number, cost: number) {
    if (!(selling > 0)) return null;
    const m = ((selling - cost) / selling) * 100;
    return (
        <Typography variant="caption" color={m >= 0 ? "success.main" : "error.main"}>
            {m.toFixed(1)}% margin
        </Typography>
    );
}

interface Props {
    product: Product;
    editable: boolean;
    save: SavePatch;
    run: RunSave;
    dirtyFor: (key: string) => (dirty: boolean) => void;
}

/**
 * The drawer's prices: selling and cost price editable in place (with the
 * margin worked out as you type), the discount and the combo prices —
 * shown, added, changed or removed without the full form.
 */
export const PricingEditor: React.FC<Props> = ({ product, editable, save, run, dirtyFor }) => {
    const selling = Number(product.selling_price);
    const cost = Number(product.cost_price);

    return (
        <>
            <Grid size={{ xs: 6 }}>
                <InlineEditField
                    layout="stat"
                    label="Selling Price"
                    display={taka(selling)}
                    statColor="primary.main"
                    raw={String(selling)}
                    type="number"
                    prefix="৳"
                    editable={editable}
                    validate={price}
                    hint={(d) => (d !== "" ? marginText(Number(d), cost) : null)}
                    onSave={(v) => save({ selling_price: Number(v) }, "Selling price saved")}
                    onDirtyChange={dirtyFor("selling")}
                />
            </Grid>
            <Grid size={{ xs: 6 }}>
                <InlineEditField
                    layout="stat"
                    label="Cost Price"
                    display={taka(cost)}
                    raw={String(cost)}
                    type="number"
                    prefix="৳"
                    editable={editable}
                    validate={price}
                    hint={(d) => (d !== "" ? marginText(selling, Number(d)) : null)}
                    onSave={(v) => save({ cost_price: Number(v) }, "Cost price saved")}
                    onDirtyChange={dirtyFor("cost")}
                />
                {marginText(selling, cost)}
            </Grid>
            <Grid size={{ xs: 12 }}>
                <SaleRow product={product} editable={editable} save={save} onDirtyChange={dirtyFor("sale")} />
                <CampaignPriceLine product={product} />
                <ComboPricesEditor product={product} editable={editable} run={run} onDirtyChange={dirtyFor("combo")} />
            </Grid>
        </>
    );
};

// ─── Discount ────────────────────────────────────────────────────────────────

const QUICK_PERCENTS = [5, 10, 15, 20];

function SaleRow({
    product,
    editable,
    save,
    onDirtyChange,
}: {
    product: Product;
    editable: boolean;
    save: SavePatch;
    onDirtyChange: (dirty: boolean) => void;
}) {
    const selling = Number(product.selling_price);
    const has = product.sale_price != null;
    const salePrice = has ? Number(product.sale_price) : null;
    const startsAt = product.sale_starts_at ? new Date(product.sale_starts_at) : null;
    const endsAt = product.sale_ends_at ? new Date(product.sale_ends_at) : null;
    const now = Date.now();

    const [open, setOpen] = useState(false);
    const [draftPrice, setDraftPrice] = useState("");
    const [draftStart, setDraftStart] = useState<Date | null>(null);
    const [draftEnd, setDraftEnd] = useState<Date | null>(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    // Another product, or a fresh copy after saving: close the panel.
    useEffect(() => {
        setOpen(false);
        setError("");
    }, [product.product_id, product.sale_price, product.sale_starts_at, product.sale_ends_at]);

    const sameTime = (a: Date | null, b: Date | null) =>
        (!a && !b) || (!!a && !!b && Math.abs(a.getTime() - b.getTime()) < 1000);
    const dirty =
        open &&
        (draftPrice !== (salePrice != null ? String(salePrice) : "") ||
            !sameTime(draftStart, startsAt) ||
            !sameTime(draftEnd, endsAt));
    useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

    const start = () => {
        setDraftPrice(salePrice != null ? String(salePrice) : "");
        setDraftStart(startsAt);
        setDraftEnd(endsAt);
        setError("");
        setOpen(true);
    };

    const submit = async () => {
        const p = Number(draftPrice);
        if (draftPrice === "" || !(p > 0)) return setError("Enter the discounted price.");
        if (p >= selling) return setError(`Must be below the selling price (${taka(selling)}).`);
        if (draftStart && draftEnd && draftEnd <= draftStart) return setError("The end must be after the start.");
        setBusy(true);
        const failed = await save(
            {
                sale_price: p,
                sale_starts_at: draftStart ? draftStart.toISOString() : null,
                sale_ends_at: draftEnd ? draftEnd.toISOString() : null,
            },
            has ? "Discount updated" : "Discount added",
        );
        setBusy(false);
        if (failed) setError(failed);
    };

    const remove = async () => {
        setBusy(true);
        const failed = await save({ sale_price: null, sale_starts_at: null, sale_ends_at: null }, "Discount removed");
        setBusy(false);
        if (failed) setError(failed);
    };

    const percentOf = (p: number) => Math.round((1 - p / selling) * 100);
    const draftNumber = Number(draftPrice);

    // What the current discount is doing, in words.
    let status = "";
    if (has) {
        if (endsAt && endsAt.getTime() <= now) status = `ended ${DATE.format(endsAt)}`;
        else if (startsAt && startsAt.getTime() > now) status = `starts ${DATE.format(startsAt)}`;
        else status = endsAt ? `until ${DATE.format(endsAt)}` : "no end date";
    }

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minHeight: 36 }}>
                <LocalOffer fontSize="small" color={product.is_on_sale ? "error" : "disabled"} />
                <Typography variant="body2" color="text.secondary">
                    Own discount
                </Typography>
                {has && salePrice != null ? (
                    <Typography variant="body2" fontWeight={600} sx={{ opacity: product.is_on_sale ? 1 : 0.6 }}>
                        <Box component="span" color="error.main">
                            {taka(salePrice)}
                        </Box>{" "}
                        · −{percentOf(salePrice)}% · {status}
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        None
                    </Typography>
                )}
                {editable && !open && (
                    <Button
                        size="small"
                        startIcon={has ? <EditOutlined fontSize="small" /> : undefined}
                        onClick={start}
                        sx={{ ml: "auto" }}
                    >
                        {has ? "Edit" : "Add discount"}
                    </Button>
                )}
            </Box>

            {open && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: 1.5, bgcolor: "action.hover" }}>
                        {product.campaign_price != null && (
                            <Alert severity="info" sx={{ mb: 1.5, py: 0 }}>
                                This is the product's own discount. Its campaign price is separate — while both run,
                                customers get the lower one, and this one carries on after the campaign.
                            </Alert>
                        )}
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <TextField
                                size="small"
                                type="number"
                                label="Discounted price"
                                value={draftPrice}
                                onChange={(e) => {
                                    setDraftPrice(e.target.value);
                                    setError("");
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        void submit();
                                    } else if (e.key === "Escape") {
                                        e.stopPropagation();
                                        setOpen(false);
                                    }
                                }}
                                autoFocus
                                inputProps={{ min: 1, step: "any", inputMode: "decimal" }}
                                InputProps={{ startAdornment: <InputAdornment position="start">৳</InputAdornment> }}
                                sx={{ width: 170, bgcolor: "background.paper" }}
                            />
                            {QUICK_PERCENTS.map((pct) => (
                                <Chip
                                    key={pct}
                                    size="small"
                                    label={`${pct}% off`}
                                    onClick={() => {
                                        setDraftPrice(String(Math.round((selling * (100 - pct)) / 100)));
                                        setError("");
                                    }}
                                />
                            ))}
                        </Stack>
                        {draftPrice !== "" && draftNumber > 0 && draftNumber < selling && (
                            <Typography variant="caption" color="success.main" fontWeight={600} display="block" sx={{ mt: 0.5 }}>
                                −{percentOf(draftNumber)}% · saves {taka(selling - draftNumber)}
                                {draftNumber < Number(product.cost_price) && (
                                    <Box component="span" color="error.main">
                                        {" "}· below cost {taka(Number(product.cost_price))}
                                    </Box>
                                )}
                            </Typography>
                        )}
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1, mt: 1.5 }}>
                            <DateTimePicker
                                label="Starts (optional)"
                                value={draftStart}
                                onChange={setDraftStart}
                                slotProps={{
                                    textField: { size: "small", helperText: "Blank starts now", sx: { bgcolor: "background.paper" } },
                                    actionBar: { actions: ["clear", "accept"] },
                                }}
                            />
                            <DateTimePicker
                                label="Ends (optional)"
                                value={draftEnd}
                                onChange={setDraftEnd}
                                slotProps={{
                                    textField: { size: "small", helperText: "Blank runs until removed", sx: { bgcolor: "background.paper" } },
                                    actionBar: { actions: ["clear", "accept"] },
                                }}
                            />
                        </Box>
                        {error && (
                            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                                {error}
                            </Typography>
                        )}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                            {has && (
                                <Link
                                    component="button"
                                    type="button"
                                    color="error"
                                    variant="body2"
                                    onClick={() => void remove()}
                                    disabled={busy}
                                    sx={{ mr: "auto" }}
                                >
                                    Remove discount
                                </Link>
                            )}
                            <Box sx={{ flex: 1 }} />
                            <Button size="small" color="inherit" onClick={() => setOpen(false)} disabled={busy}>
                                Cancel
                            </Button>
                            <Button size="small" variant="contained" onClick={() => void submit()} disabled={busy}>
                                {busy ? "Saving…" : "Save discount"}
                            </Button>
                        </Box>
                    </Paper>
                </LocalizationProvider>
            )}
        </Box>
    );
}

/**
 * The campaign's price for this product (set in the campaign, not here):
 * when it runs, and whether the product's own discount undercuts it.
 */
function CampaignPriceLine({ product }: { product: Product }) {
    if (product.campaign_price == null || !product.sale_campaign_id) return null;
    const selling = Number(product.selling_price);
    const price = Number(product.campaign_price);
    const startsAt = product.campaign_price_starts_at ? new Date(product.campaign_price_starts_at) : null;
    const endsAt = product.campaign_price_ends_at ? new Date(product.campaign_price_ends_at) : null;
    const now = Date.now();
    const ended = !!endsAt && endsAt.getTime() <= now;
    const upcoming = !!startsAt && startsAt.getTime() > now;
    const status = ended
        ? `ended ${DATE.format(endsAt!)}`
        : upcoming
          ? `starts ${DATE.format(startsAt!)}`
          : endsAt
            ? `until ${DATE.format(endsAt)}`
            : "until the campaign ends";
    const name = product.live_campaign?.name ?? "Campaign";
    const effective = Number(product.effective_price);
    const ownWins = !ended && !upcoming && product.is_on_sale && effective < price;

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minHeight: 36, opacity: ended ? 0.6 : 1 }}>
            <Campaign fontSize="small" color={ended || upcoming ? "disabled" : "secondary"} />
            <Typography variant="body2" color="text.secondary">
                {name} price
            </Typography>
            <Typography variant="body2" fontWeight={600}>
                <Box component="span" color="secondary.main">
                    {taka(price)}
                </Box>{" "}
                · −{Math.round((1 - price / selling) * 100)}% · {status}
            </Typography>
            {ownWins && <Chip size="small" color="warning" variant="outlined" label={`Own discount is lower — customers pay ${taka(effective)}`} />}
            <Button size="small" component={RouterLink} to={`/campaigns/${product.sale_campaign_id}`} sx={{ ml: "auto" }}>
                Open campaign
            </Button>
        </Box>
    );
}

export default PricingEditor;
